<template>
    <div id="app">
        <maptm-header></maptm-header>
        <div class="card" v-if="$isMobile()">
            <div class="card-content">
                <p class="title">
                    <small>Mobile device detected</small>
                </p>
                <p class="content">
                    It looks like your trying to use this webapp on a mobile device.<br>
                    Unfortunately this webapp is not optimized for mobile devices.<br>
                    Try using this webapp on a desktop device.
                </p>
            </div>
        </div>
        <div v-else>
            <manual-modal></manual-modal>
            <feedback-modal></feedback-modal>
            <login-modal></login-modal>
            <sign-up-modal></sign-up-modal>
            <watch-movie-modal></watch-movie-modal>
            <div class="section" id="container">
                <div class="columns">

                    <div class="column is-9">
                        <div id="map-wrap" style="height: 90vh">
                            <l-map
                                ref="myMap"
                                :zoom="zoom"
                                :center="[50.82, 4.38]"
                                :options="{wheelPxPerZoomLevel:120}"
                                @update:zoom="zoomUpdated"
                                @update:center="centerUpdated"
                                @update:bounds="boundsUpdated"
                                @baselayerchange="baseLayerChanged"
                                style="z-index:0">
                                <l-control-layers></l-control-layers>
                                <l-tile-layer
                                    v-for="tileProvider in tileProviders"
                                    :key="tileProvider.name"
                                    :name="tileProvider.name"
                                    :visible="tileProvider.visible"
                                    :url="tileProvider.url"
                                    :attribution="tileProvider.attribution"
                                    layer-type="base"/>
                            </l-map>
                        </div>
                    </div>

                    <div class="column is-3">
                        <b-field label="Start">
                            <b-datetimepicker
                                v-model="datetime_start"
                                placeholder="Click to select..."
                                :min-datetime="datetime_min"
                                :max-datetime="datetime_max"
                                :mobile-native="false">
                            </b-datetimepicker>
                        </b-field>
                        <b-field label="End">
                            <b-datetimepicker
                                v-model="datetime_end"
                                placeholder="Click to select..."
                                :min-datetime="datetime_min"
                                :max-datetime="datetime_max"
                                :mobile-native="false">
                            </b-datetimepicker>
                        </b-field>
                        <div class="field" v-if="adminPanel">
                            <b-checkbox v-model="message.logo">
                                MAPtm logo
                            </b-checkbox>
                        </div>
                        <div class="field">
                            <b-checkbox v-model="message.highway">
                                Highway
                            </b-checkbox>
                        </div>
                        <div class="field">
                            <b-checkbox v-model="message.road">
                                Road
                            </b-checkbox>
                        </div>
                        <div class="field">
                            <b-checkbox v-model="message.street">
                                Street
                            </b-checkbox>
                        </div>
                        <div class="field">
                            <b-checkbox v-model="message.jam">
                                Jam
                            </b-checkbox>
                        </div>
                        <div class="field">
                            <b-checkbox v-model="message.road_closed">
                                Road closed
                            </b-checkbox>
                        </div>
                        <div class="field">
                            <b-checkbox v-model="message.accidents">
                                Accidents
                            </b-checkbox>
                        </div>                  
                        <div class="field">
                            <b-checkbox v-model="message.weather">
                                Rain/snow
                            </b-checkbox>
                        </div>
                        <b-field label="Frames per second" v-if="adminPanel">
                            <b-numberinput min="1" max="18" v-model="message.fps"></b-numberinput>
                        </b-field>
                        <div class="buttons">
                            <b-button class="is-link is-large" :disabled="movie_generating_disabled || !signedIn || creditsLeft < 1" @click="sendMessage()">
                                <b-icon icon="cogs"></b-icon>
                                <span>Generate</span>
                            </b-button>
                            <b-button class="is-link is-large" tag="a" :disabled="!download_ready" @click="changeMovieWatchModalState">
                                <b-icon icon="cloud-download"></b-icon>
                                <span>Watch movie</span>
                            </b-button>
                        </div>
                        <div v-if="signedIn" class="credits">
                            <p class="title"> {{creditsLeft}} credits left</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <loading 
            :active.sync="movie_generating_disabled"
            :color="'#f15a24'"
            :loader="'bars'"
            :width="128"
            :height="128">
            <div class="after-icon" slot="after">
                <h2>{{socketMessage}}</h2>
            </div>
        </loading>
    </div>
</template>

<script>
    import { Auth } from 'aws-amplify'
    import { AmplifyEventBus } from 'aws-amplify-vue'

    import Loading from 'vue-loading-overlay'
    import 'vue-loading-overlay/dist/vue-loading.css'

    import MaptmHeader from '@/components/MaptmHeader.vue'
    import ManualModal from '@/components/ManualModal.vue'
    import FeedbackModal from '@/components/FeedbackModal.vue'
    import LoginModal from '@/components/LoginModal.vue'
    import SignUpModal from '@/components/SignUpModal.vue'
    import WatchMovieModal from '@/components/WatchMovieModal.vue'

    export default {
        name: 'app',

        components: {
            MaptmHeader,
            ManualModal,
            FeedbackModal,
            LoginModal,
            SignUpModal,
            WatchMovieModal,
            Loading
        },

        data () {
            const max = new Date()
            max.setMinutes(max.getMinutes() - 5)
            const min = new Date('April 13, 2020')
            const dtstrt = new Date()
            dtstrt.setMinutes(dtstrt.getMinutes() - 65)
            return {
                tileProviders: [
                    {
                        name: 'Dark',
                        visible: true,
                        url: 'https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png',
                    },
                    {
                        name: 'Light',
                        visible: false,
                        url: 'https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png',
                    }
                ],
                current_layer: null,
                isConnected: false,
                zoom: 8,
                center: '',
                bounds: '',
                socketMessage: '',
                message: {
                    logo: true,
                    zoom: '',
                    lat_min: '',
                    long_min: '',
                    lat_max: '',
                    long_max: '',
                    datetime_start: '',
                    datetime_end: '',
                    highway: true,
                    road: false,
                    street: false,
                    jam: true,
                    road_closed: true,
                    weather: false,
                    accidents: true,
                    bridges: false,
                    fps: 6,
                    layer: null,
                    user_id: null,
                },
                datetime_start: dtstrt,
                datetime_end: max,
                datetime_min: min,
                datetime_max: max,
                download_url: '',
                download_ready: false,
                movie_generating_disabled: false,
                movie_generating: false,
                username: '',
                password: '',
                newPasswordRequired: false,
                newPassword: ''
            }
        },

        computed: {
            socketConnected () {
                return this.$store.state.socket.isConnected
            },

            signedIn () {
                return this.$store.state.signedIn
            },

            userName () {
                return this.$store.state.user.attributes.name
            },

            userId () {
                return this.$store.state.user.attributes.sub
            },

            creditsLeft () {
                return this.$store.state.credits
            },

            adminPanel () {
                return this.$store.getters.showAdminPanel
            },

            formattedStart () {
                const year = this.datetime_start.getFullYear()
                const month = ('0' + (this.datetime_start.getMonth() + 1)).slice(-2)
                const date = ('0' + this.datetime_start.getDate()).slice(-2)
                const hours = ('0' + this.datetime_start.getHours()).slice(-2)
                const minutes = ('0' + this.datetime_start.getMinutes()).slice(-2)
                return year + '-' + month + '-' + date + ' ' + hours + ':' + minutes
            },

            formattedEnd () {
                const year = this.datetime_end.getFullYear()
                const month = ('0' + (this.datetime_end.getMonth() + 1)).slice(-2)
                const date = ('0' + this.datetime_end.getDate()).slice(-2)
                const hours = ('0' + this.datetime_end.getHours()).slice(-2)
                const minutes = ('0' + this.datetime_end.getMinutes()).slice(-2)
                return year + '-' + month + '-' + date + ' ' + hours + ':' + minutes
            }
        },

        watch: {
            socketConnected(newValue, oldValue) {
                if (newValue === false) {
                    this.$buefy.toast.open({
                        duration: 5000,
                        message: 'Disconnected!',
                        type: 'is-danger',
                        position: 'is-bottom'

                    })
                } else {
                    this.$buefy.toast.open({
                        message: 'Connected!',
                        type: 'is-success',
                        position: 'is-bottom'
                    })
                }
            }
        },

        mounted () {
            if ( !this.$isMobile() ) {
                this.findUser();

                AmplifyEventBus.$on('authState', info => {
                    if (info === 'signedIn') {
                        this.findUser();
                    } else {
                        this.$store.state.signedIn = false;
                        this.$store.state.user = null;
                    }
                });

                this.$nextTick(() => {
                    this.bounds = this.$refs.myMap.mapObject.getBounds()
                    this.message.lat_min = this.bounds._southWest.lat
                    this.message.long_min = this.bounds._southWest.lng
                    this.message.lat_max = this.bounds._northEast.lat
                    this.message.long_max = this.bounds._northEast.lng
                    this.message.zoom = this.zoom
                })
            }
        },

        methods: {
            sendMessage () {
                // sending data
                if (this.datetime_start >= this.datetime_end) {
                    alert('Kies een starttijd die eerder is dan de eindtijd')
                } else if (this.differenceHours(this.datetime_end, this.datetime_start) > 12) {
                    alert('Kies een verschil tussen de starttijd en eindtijd van maximaal 12 uur')
                } else if (this.signedIn === true && this.creditsLeft > 0) {
                    this.download_ready = false

                    this.movie_generating_disabled = true
                    this.movie_generating = true

                    this.message.datetime_start = this.formattedStart
                    this.message.datetime_end = this.formattedEnd
                    this.message.user_id = this.userId

                    this.$socket.send(JSON.stringify(this.message))

                    this.socketMessage = 'request sent...'

                    this.$socket.onmessage = (data) => {
                        if ('url' in JSON.parse(data.data)) {
                            this.socketMessage = 'almost done...'
                            this.$store.state.movie_url = JSON.parse(data.data).url
                            this.axios
                                .get(this.$store.state.apiUrl + '/credit_used/' + this.$store.state.user.username)
                                .then(data => {
                                    this.$store.state.credits = data.data.credits
                                    this.download_ready = true
                                })
                                .catch(err => {
                                    this.$buefy.dialog.alert({
                                        title: 'Error',
                                        message: err.message,
                                        type: 'is-danger',
                                        hasIcon: true
                                    })
                                })
                                .then(() => {
                                    this.movie_generating_disabled = false
                                    this.changeMovieWatchModalState()
                                })
                        } else if ('status' in JSON.parse(data.data)) {
                            this.socketMessage = JSON.parse(data.data).status
                        } else {
                            console.log(JSON.parse(data.data))
                        }
                    }
                }
            },

            changeMovieWatchModalState () {
                this.$store.commit('toggleWatchMovieModal')
            },

            zoomUpdated (zoom) {
                this.zoom = zoom
                this.message.zoom = zoom
            },

            centerUpdated (center) {
                this.center = center
            },

            boundsUpdated (bounds) {
                this.bounds = bounds
                this.message.lat_min = bounds._southWest.lat
                this.message.long_min = bounds._southWest.lng
                this.message.lat_max = bounds._northEast.lat
                this.message.long_max = bounds._northEast.lng
            },

            baseLayerChanged (layer) {
                this.message.layer = layer.name
            },

            differenceHours (dt2, dt1) {
                let diff = (dt2.getTime() - dt1.getTime()) / 1000
                diff /= (60 * 60)
                return Math.abs(Math.round(diff))
            },

            async findUser() {
                try {
                    const user = await Auth.currentAuthenticatedUser();
                    this.$store.state.signedIn = true;
                    this.$store.state.user = user;
                    this.axios
                        .get(this.$store.state.apiUrl + '/get_user/' + user.username)
                        .then(data => {
                            console.log(data.data.credits)
                            this.$store.state.credits = data.data.credits
                        })
                        .catch(err => {
                            this.$buefy.dialog.alert({
                                title: 'Error',
                                message: err.message,
                                type: 'is-danger',
                                hasIcon: true
                            })
                        })
                } catch(err) {
                    this.$store.state.signedIn = false;
                    this.$store.state.user = null;
                }
            }
        }
    }
</script>

<style>
    #container {
        padding-top: 1.5rem;
        padding-right: 1.5rem;
        padding-bottom: 1.5rem;
        padding-left: 1.5rem;
    },

    #credits {
        height: 100%;
    }

    .after-icon {
        position: absolute;
        left: -36%;
        width: 200px;
        text-align: center;
    }
</style>
